import React, { useEffect, useState, useCallback } from 'react';
import { ClassificationResultsResponse, ClassificationResultItem } from 'types/document.types';
import { TableHeader } from 'components/atoms/TableHeader';
import { HistoryTableRow } from 'components/molecules/HistoryTableRow';
import LoadingSpinner from 'components/atoms/LoadingSpinner';
import { documentService } from 'services/documentService';
import useAuthStore from 'stores/authStore';

interface HistoryTableProps {
  onRowClick?: (item: ClassificationResultItem) => void;
  limit?: number;
}

export const HistoryTable: React.FC<HistoryTableProps> = ({
  onRowClick,
  limit = 10
}) => {
  const [data, setData] = useState<ClassificationResultsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const userId = useAuthStore((state) => state.getUserId());

  const fetchResults = useCallback(async (newOffset: number = 0) => {
    if (!userId) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await documentService.getClassificationResults(userId, limit, newOffset);
      setData(response);
      setOffset(newOffset);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch classification results');
    } finally {
      setLoading(false);
    }
  }, [userId, limit]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const handlePrevious = () => {
    if (offset > 0) {
      fetchResults(Math.max(0, offset - limit));
    }
  };

  const handleNext = () => {
    if (data && offset + limit < data.total_count) {
      fetchResults(offset + limit);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800 font-medium">Error loading results</p>
        <p className="text-red-600 text-sm mt-2">{error}</p>
        <button
          onClick={() => fetchResults(offset)}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.results.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
        <p className="text-gray-600 text-lg">No classification results yet</p>
        <p className="text-gray-500 text-sm mt-2">
          Your completed classifications will appear here
        </p>
      </div>
    );
  }

  const startIndex = offset + 1;
  const endIndex = Math.min(offset + data.results.length, data.total_count);

  return (
    <div className="space-y-4">
      {/* Stats Bar */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          Showing <span className="font-medium text-gray-900">{startIndex}</span> to{' '}
          <span className="font-medium text-gray-900">{endIndex}</span> of{' '}
          <span className="font-medium text-gray-900">{data.total_count}</span> results
        </div>
        <button
          onClick={() => fetchResults(offset)}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          Refresh
        </button>
      </div>

      {/* Table Container */}
      <div className="overflow-x-auto shadow-md rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <TableHeader>Query</TableHeader>
              <TableHeader>HTS Code</TableHeader>
              <TableHeader align="center">Confidence</TableHeader>
              <TableHeader>Base Duty</TableHeader>
              <TableHeader>Date</TableHeader>
              <TableHeader align="center">Actions</TableHeader>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.results.map((item) => (
              <HistoryTableRow
                key={item.classification_id}
                item={item}
                onClick={onRowClick}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={handlePrevious}
            disabled={offset === 0}
            className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
              offset === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            disabled={offset + limit >= data.total_count}
            className={`relative ml-3 inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
              offset + limit >= data.total_count
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Page{' '}
              <span className="font-medium">{Math.floor(offset / limit) + 1}</span> of{' '}
              <span className="font-medium">{Math.ceil(data.total_count / limit)}</span>
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={handlePrevious}
                disabled={offset === 0}
                className={`relative inline-flex items-center rounded-l-md px-4 py-2 text-sm font-medium ${
                  offset === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-900 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Previous
              </button>
              <button
                onClick={handleNext}
                disabled={offset + limit >= data.total_count}
                className={`relative inline-flex items-center rounded-r-md px-4 py-2 text-sm font-medium ${
                  offset + limit >= data.total_count
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-900 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};
